/**
 * AssignableRow — Inline-Picker für User-Zuweisung (PL/Manager/MTB).
 * Quelle: §10.18 Extraktion aus project-detail.tsx Right Rail.
 *
 * Read-Modus: Avatar + Name oder `◇ frei`-Badge.
 * Edit-Modus: Suche + Liste mit max 50 Treffern, click-outside schließt.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nameInitials } from '../../lib/avatar.js';
import { anzeigeName } from '../../lib/nutzer-anzeige.js';

/** `active` fehlt in Teil-Objekten → gilt als aktiv. */
export type AssignableUser = {
  user_id: string;
  name: string;
  email: string;
  active?: boolean | undefined;
};

export function AssignableRow({
  label,
  userId,
  users,
  canEdit,
  onAssign,
  disabledReason,
  required,
}: {
  label: string;
  userId?: string | null | undefined;
  users: Array<AssignableUser>;
  canEdit: boolean;
  onAssign: (userId: string | null) => void;
  /**
   * Begruendung, warum die Zeile nicht bedienbar ist — ersetzt den allgemeinen
   * „Sie haben Leserechte"-Hinweis (#78: Zuteilungsschutz in der Eingangsphase).
   *
   * Die Zeile wird ausgegraut **mit** Begruendung dargestellt und nicht ausgeblendet: Ein
   * verschwundenes Feld liest sich als „gibt es nicht", ein gesperrtes als „nicht fuer dich" —
   * Letzteres ist die Wahrheit.
   */
  disabledReason?: string | undefined;
  /**
   * Unterdrueckt „Zuweisung entfernen". Fuer Felder, die eine Person haben MUESSEN.
   *
   * Projektleiter und Fachbereichsleiter sind in Salesforce durch eine Validierungsregel
   * pflichtig („Es muss ein Projektleiter erfasst werden."). Ein Leeren in der
   * Projektverwaltung liesse sich deshalb nie uebertragen und wuerde nur eine dauerhaft
   * scheiternde Uebertragung erzeugen. Besser gar nicht erst anbieten.
   */
  required?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const currentUser = userId ? users.find((u) => u.user_id === userId) : undefined;

  useEffect(() => {
    if (!editing) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setEditing(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [editing]);

  // #116: Kandidaten nur aktive Nutzer. `currentUser` (oben) kommt bewusst aus der UNGEFILTERTEN
  // Liste: ein eingetragener, inzwischen ausgeschiedener Nutzer soll als „Name (inaktiv)" stehen,
  // nicht als „◇ frei".
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kandidaten = users.filter((u) => u.active !== false);
    if (!q) return kandidaten.slice(0, 50);
    return kandidaten
      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 50);
  }, [users, query]);

  if (!editing) {
    return (
      <div className="flex items-center justify-between py-1.5 text-sm">
        <span style={{ color: 'var(--ink-3)' }}>{label}</span>
        <button
          type="button"
          onClick={() => canEdit && setEditing(true)}
          disabled={!canEdit}
          title={
            !canEdit
              ? (disabledReason ?? t('v2.reader.tooltip', 'Sie haben Leserechte'))
              : t('v2.detail.assign_edit', 'Zuweisen')
          }
          className="flex items-center gap-1.5"
          style={{ cursor: canEdit ? 'pointer' : 'not-allowed', opacity: canEdit ? 1 : 0.6 }}
        >
          {currentUser ? (
            <>
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-medium"
                style={{ background: 'var(--surface-3)', color: 'var(--ink-2)' }}
              >
                {nameInitials(currentUser.name)}
              </span>
              <span style={{ color: 'var(--ink-2)', fontSize: 12 }}>
                {anzeigeName(currentUser, t)}
              </span>
            </>
          ) : (
            <span
              className="text-[11px] px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--danger-soft)',
                color: 'var(--danger)',
                fontFamily: 'var(--f-mono)',
              }}
            >
              {t('v2.detail.rail_free', '◇ frei')}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="py-1.5 relative">
      <div className="flex items-center justify-between text-sm mb-1">
        <span style={{ color: 'var(--ink-3)' }}>{label}</span>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-[11px]"
          style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
        >
          {t('v2.modal.cancel', 'Abbrechen')}
        </button>
      </div>
      <input
        type="text"
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('v2.detail.assign_search', 'Name oder E-Mail suchen…')}
        className="w-full text-xs px-2 py-1.5 rounded outline-none"
        style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--line-soft)',
          color: 'var(--ink)',
        }}
      />
      <div
        className="mt-1 max-h-60 overflow-y-auto rounded"
        style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)' }}
      >
        {currentUser && !required && (
          <button
            type="button"
            onClick={() => {
              onAssign(null);
              setEditing(false);
            }}
            className="w-full text-left px-2 py-1.5 text-[11px] border-b"
            style={{
              color: 'var(--danger)',
              borderColor: 'var(--line-soft)',
              fontFamily: 'var(--f-mono)',
            }}
          >
            ✕ {t('v2.detail.assign_clear', 'Zuweisung entfernen')}
          </button>
        )}
        {filtered.length === 0 ? (
          <div className="px-2 py-2 text-[11px]" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.assign_no_match', 'Keine Treffer.')}
          </div>
        ) : (
          filtered.map((u) => (
            <button
              key={u.user_id}
              type="button"
              onClick={() => {
                onAssign(u.user_id);
                setEditing(false);
              }}
              className="w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-[var(--surface-2)]"
              style={{ color: 'var(--ink)' }}
            >
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-medium shrink-0"
                style={{ background: 'var(--surface-3)', color: 'var(--ink-2)' }}
              >
                {nameInitials(u.name)}
              </span>
              <span className="flex-1 truncate">{u.name}</span>
              <span className="text-[10px]" style={{ color: 'var(--ink-3)' }}>
                {u.email}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
